import React from "react";
import { Check, ArrowUpRight, Award, Users, Globe, Workflow, MessagesSquare } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";

const CALENDLY = "https://calendly.com/ankitagogia/30min";

const goToContact = () => {
  const el = document.querySelector("#contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const PACKAGES = [
  {
    bracket: "Entry-Point Sprint",
    name: "Brand Identity Sprint",
    price: "$600",
    note: "Fixed scope",
    desc: "A fast, focused sprint that gives your business a complete, professional visual identity — without the agency timeline or hourly billing.",
    features: [
      "Brand Discovery",
      "Logo Design",
      "Visual Identity",
      "Color Palette & Typography",
      "Brand Guidelines",
      "Social Media Profile Assets",
      "10-Day Delivery",
    ],
    cta: "Start Your Sprint",
    action: () => window.open(CALENDLY, "_blank", "noopener,noreferrer"),
    popular: false,
  },
  {
    bracket: "Flagship Core",
    name: "Brand Strategy & Identity",
    price: "$1,200",
    note: "Fixed scope",
    desc: "The complete brand foundation: strategic clarity and a distinctive visual system, designed to grow with your business.",
    features: [
      "Brand Strategy Workshop",
      "Brand Positioning",
      "Messaging Direction",
      "Logo Design",
      "Visual Identity System",
      "Brand Guidelines",
      "Creative Direction",
      "Launch Assets",
    ],
    cta: "Book a Discovery Call",
    action: () => window.open(CALENDLY, "_blank", "noopener,noreferrer"),
    popular: true,
  },
  {
    bracket: "Integrated Retainer",
    name: "Creative Partner Retainer",
    price: "$1,000",
    suffix: "/month",
    note: "Ongoing",
    desc: "Your embedded creative partner — continuous design support and creative direction for founders and growing businesses.",
    features: [
      "Social Media Design",
      "Presentation Design",
      "Marketing Collateral",
      "Creative Direction",
      "Monthly Design Support",
      "Priority Turnaround",
    ],
    cta: "Let's Work Together",
    action: goToContact,
    popular: false,
  },
];

const LINKEDIN_PACKAGES = [
  {
    bracket: "Quick Refresh",
    name: "Basic Profile",
    price: "$150",
    note: "Starting at",
    desc: "A polished, professional presence that makes a strong first impression on LinkedIn.",
    features: [
      "Custom Banner Design",
      "Optimised Headline",
      "Profile Photo Enhancement",
      "About Section Copy",
      "2 Design Revisions",
      "3-Day Delivery",
    ],
    cta: "Get Started",
    action: goToContact,
    popular: false,
  },
  {
    bracket: "Visibility Builder",
    name: "Growth Profile",
    price: "$300",
    note: "Starting at",
    desc: "For founders and experts who want visibility, credibility and inbound opportunities.",
    features: [
      "Custom Banner Design",
      "Optimised Headline & About Section",
      "3 Featured Section Graphics",
      "5 Branded Post Templates",
      "Positioning Strategy Session",
      "3 Design Revisions",
      "5-Day Delivery",
    ],
    cta: "Book a Discovery Call",
    action: () => window.open(CALENDLY, "_blank", "noopener,noreferrer"),
    popular: true,
  },
  {
    bracket: "Authority Suite",
    name: "Premium Profile",
    price: "$500",
    note: "Starting at",
    desc: "A complete LinkedIn presence for thought leaders and executive personal branding.",
    features: [
      "Custom Banner + Alternate Banner",
      "Positioning Strategy Session",
      "Headline, About & Experience Copy",
      "3 Featured Section Graphics",
      "10 Branded Post Templates",
      "Content Direction & Voice Guidelines",
      "Unlimited Design Revisions",
      "Priority 10-Day Delivery",
    ],
    cta: "Start Elevating",
    action: goToContact,
    popular: false,
  },
];

const TRUST = [
  { icon: Award, label: "11+ Years of Experience" },
  { icon: Users, label: "20+ Global Clients" },
  { icon: Globe, label: "USA · UK · UAE · Australia" },
  { icon: Workflow, label: "Strategy-Led Creative Process" },
  { icon: MessagesSquare, label: "Transparent Communication" },
];

const PricingCard = ({ p }) => (
  <div
    className={`relative flex h-full flex-col rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${
      p.popular
        ? "bg-charcoal text-cream border border-gold/50 shadow-[0_40px_80px_-40px_rgba(122,31,42,0.5)] lg:-mt-4 lg:mb-4"
        : "bg-white border border-charcoal/5 shadow-[0_20px_50px_-40px_rgba(34,34,34,0.4)] hover:border-gold/40"
    }`}
    data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    {p.popular && (
      <span className="absolute -top-3 left-8 rounded-full bg-gold text-charcoal text-[11px] font-semibold tracking-[0.15em] uppercase px-4 py-1.5">
        Most Popular
      </span>
    )}
    {p.bracket && (
      <span className={`text-[11px] tracking-[0.2em] uppercase font-semibold ${p.popular ? "text-gold" : "text-burgundy/70"}`}>
        {p.bracket}
      </span>
    )}
    <h3 className={`mt-2 font-heading text-2xl leading-tight ${p.popular ? "text-cream" : "text-charcoal"}`}>
      {p.name}
    </h3>
    <div className="mt-5 flex items-end gap-1">
      <span className={`text-xs tracking-wide uppercase mb-2 mr-1 ${p.popular ? "text-cream/50" : "text-charcoal/40"}`}>
        {p.note}
      </span>
      <span className={`font-sans font-semibold text-4xl leading-none tracking-tight ${p.popular ? "text-gold" : "text-burgundy"}`}>
        {p.price}
      </span>
      {p.suffix && (
        <span className={`text-sm mb-1 ${p.popular ? "text-cream/60" : "text-charcoal/50"}`}>{p.suffix}</span>
      )}
    </div>
    <p className={`mt-4 text-sm leading-relaxed ${p.popular ? "text-cream/70" : "text-charcoal/60"}`}>
      {p.desc}
    </p>
    <div className={`my-6 h-px w-full ${p.popular ? "bg-cream/15" : "bg-charcoal/10"}`} />
    <ul className="flex-1 space-y-3">
      {p.features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${p.popular ? "bg-gold/20 text-gold" : "bg-burgundy/10 text-burgundy"}`}>
            <Check size={12} strokeWidth={3} />
          </span>
          <span className={`text-sm ${p.popular ? "text-cream/85" : "text-charcoal/75"}`}>{f}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={p.action}
      className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 ${
        p.popular
          ? "bg-gold text-charcoal hover:bg-cream"
          : "bg-charcoal text-cream hover:bg-burgundy"
      }`}
      data-testid={`pricing-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {p.cta}
      <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
    </button>
  </div>
);

export const Pricing = () => {
  return (
    <section id="pricing" className="py-24 md:py-32 lg:py-40 bg-cream" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">Pricing & Packages</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal">
              Three clear ways to <span className="italic text-burgundy">work together.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-charcoal/70 leading-relaxed">
              Fixed-scope, outcome-first packages — no hourly billing, no surprise invoices.
              Pick the bracket that matches where your business is today.
            </p>
          </Reveal>
        </div>

        {/* Cards */}
        <StaggerGroup className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch" stagger={0.08}>
          {PACKAGES.map((p) => (
            <StaggerItem key={p.name} className="h-full">
              <PricingCard p={p} />
            </StaggerItem>
          ))}
        </StaggerGroup>

        {/* LinkedIn Personal Branding sub-section */}
        <div className="mt-24 md:mt-32" data-testid="linkedin-pricing">
          <div className="max-w-3xl">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-gold" />
                <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">
                  LinkedIn Personal Branding
                </span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-charcoal">
                Show up like the expert <span className="italic text-burgundy">you already are.</span>
              </h3>
              <p className="mt-5 text-base md:text-lg text-charcoal/70 leading-relaxed">
                A LinkedIn profile is now a founder's front door. Whether you're building authority,
                attracting clients, or preparing to raise — I'll craft a presence that opens the right doors.
              </p>
            </Reveal>
          </div>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch" stagger={0.08}>
            {LINKEDIN_PACKAGES.map((p) => (
              <StaggerItem key={p.name} className="h-full">
                <PricingCard p={p} />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>

        {/* Custom solution CTA */}
        <Reveal>
          <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-3xl border border-charcoal/10 bg-white/50 backdrop-blur-sm p-8 md:p-10">
            <div className="max-w-2xl">
              <h3 className="font-heading text-2xl md:text-3xl text-charcoal">Need a custom solution?</h3>
              <p className="mt-2 text-sm md:text-base text-charcoal/60 leading-relaxed">
                Looking for website or landing page design, or something that falls outside these
                brackets? Every project is different — I'd be happy to create a tailored proposal
                based on your goals, timeline, and scope.
              </p>
            </div>
            <button
              onClick={goToContact}
              className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-charcoal/20 text-charcoal hover:border-burgundy hover:text-burgundy px-8 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
              data-testid="pricing-custom-quote"
            >
              Request a Custom Quote
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </Reveal>

        {/* Trust row */}
        <StaggerGroup className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-5" stagger={0.05}>
          {TRUST.map((t) => {
            const Icon = t.icon;
            return (
              <StaggerItem key={t.label}>
                <div className="flex items-center gap-2.5">
                  <Icon size={18} strokeWidth={1.6} className="text-gold" />
                  <span className="text-sm text-charcoal/70">{t.label}</span>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
};
